import { useEffect, useState, useCallback } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Plus, X, Search, ArrowUpDown } from "lucide-react";
import { format, parseISO } from "date-fns";
import { Pagination } from "@/components/Pagination";

function formatDate(d) { if (!d) return "—"; try { return format(parseISO(d), "dd MMM yyyy"); } catch { return d; } }

function StatusPill({ status }) {
  const s = status || "pending";
  const cls = s === "confirmed" ? "bg-[#829384]/10 text-[#829384] border-[#829384]/30"
    : s === "lost" ? "bg-red-50 text-red-700 border-red-200"
    : "bg-[#D4A373]/10 text-[#D4A373] border-[#D4A373]/30";
  return <span className={`px-2 py-0.5 text-[10px] uppercase tracking-[0.2em] border rounded-full ${cls}`}>{s}</span>;
}

function NewTrialModal({ open, onClose, onSaved, weddings, users }) {
  const initial = { wedding_id: "", title: "Trial", event_date: "", start_time: "", location: "", notes: "", assigned: [] };
  const [form, setForm] = useState(initial);
  const [search, setSearch] = useState("");
  const [assignSel, setAssignSel] = useState("");
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (open) { setForm(initial); setSearch(""); } /* eslint-disable-next-line */ }, [open]);
  if (!open) return null;

  const term = search.trim().toLowerCase();
  const matches = term ? weddings.filter((w) =>
    (w.couple_names || "").toLowerCase().includes(term) ||
    (w.contact_phone || "").toLowerCase().includes(term) ||
    (w.instagram_id || "").toLowerCase().includes(term) ||
    (w.location || "").toLowerCase().includes(term)
  ).slice(0, 8) : [];
  const sel = weddings.find((w) => w.id === form.wedding_id);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.wedding_id) { toast.error("Please pick a wedding lead"); return; }
    setSaving(true);
    try {
      await api.post("/events", {
        wedding_id: form.wedding_id, title: form.title || "Trial", event_type: "trial",
        event_date: form.event_date, start_time: form.start_time, location: form.location,
        notes: form.notes, assigned: form.assigned.map((a) => ({ ...a, agreed_amount: 0 })),
      });
      toast.success("Trial scheduled");
      onSaved(); onClose();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-start md:items-center justify-center p-4 overflow-y-auto" data-testid="trial-form-modal">
      <div className="bg-white border border-[#EAE7E0] max-w-xl w-full my-8">
        <div className="flex items-center justify-between p-6 border-b border-[#EAE7E0]">
          <div>
            <div className="text-[10px] uppercase tracking-[0.3em] text-[#5C5652]">Schedule</div>
            <h2 className="font-serif text-3xl tracking-tight">New Trial</h2>
          </div>
          <button onClick={onClose} className="text-[#5C5652] hover:text-[#2A2421]" data-testid="close-trial-modal"><X size={20} /></button>
        </div>
        <form onSubmit={submit} className="p-6 space-y-5">
          <div>
            <label className="block text-[10px] uppercase tracking-[0.25em] text-[#5C5652] mb-2">Wedding * <span className="text-[#5C5652]/60 normal-case tracking-normal">(pending or confirmed)</span></label>
            {sel ? (
              <div className="flex items-center justify-between border border-[#EAE7E0] p-3 bg-[#FAF9F6]" data-testid="trial-selected-wedding">
                <div>
                  <div className="text-sm text-[#2A2421]">{sel.couple_names} <StatusPill status={sel.status} /></div>
                  <div className="text-xs text-[#5C5652]">{sel.contact_phone || "—"} · {sel.location || ""}</div>
                </div>
                <button type="button" onClick={() => setForm({ ...form, wedding_id: "" })} className="text-xs uppercase tracking-[0.2em] text-[#5C5652] hover:text-red-700">Change</button>
              </div>
            ) : (
              <div className="relative">
                <div className="flex items-center gap-2 border-b border-[#EAE7E0] py-2">
                  <Search size={14} className="text-[#5C5652]" />
                  <input value={search} onChange={(e) => setSearch(e.target.value)} data-testid="trial-wedding-search"
                    placeholder="Search by couple name or phone…" className="flex-1 bg-transparent focus:outline-none" />
                </div>
                {matches.length > 0 && (
                  <div className="absolute z-10 top-full left-0 right-0 bg-white border border-[#EAE7E0] mt-1 max-h-60 overflow-y-auto">
                    {matches.map((w) => (
                      <button key={w.id} type="button" onClick={() => { setForm({ ...form, wedding_id: w.id }); setSearch(""); }}
                        data-testid={`trial-search-option-${w.id}`}
                        className="w-full text-left px-4 py-3 hover:bg-[#FAF9F6] border-b border-[#EAE7E0] last:border-0">
                        <div className="flex items-center gap-2 text-sm text-[#2A2421]">{w.couple_names} <StatusPill status={w.status} /></div>
                        <div className="text-xs text-[#5C5652]">{w.contact_phone || "no phone"} · {w.location || "—"}</div>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-[10px] uppercase tracking-[0.25em] text-[#5C5652] mb-2">Date *</label>
              <input required type="date" value={form.event_date} onChange={(e) => setForm({ ...form, event_date: e.target.value })} data-testid="trial-date-input"
                className="w-full border-b border-[#EAE7E0] bg-transparent py-2 focus:border-[#C88270] focus:outline-none" />
            </div>
            <div>
              <label className="block text-[10px] uppercase tracking-[0.25em] text-[#5C5652] mb-2">Start time</label>
              <input type="time" value={form.start_time} onChange={(e) => setForm({ ...form, start_time: e.target.value })} data-testid="trial-time-input"
                className="w-full border-b border-[#EAE7E0] bg-transparent py-2 focus:border-[#C88270] focus:outline-none" />
            </div>
          </div>
          <div>
            <label className="block text-[10px] uppercase tracking-[0.25em] text-[#5C5652] mb-2">Location</label>
            <input value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} data-testid="trial-location-input"
              className="w-full border-b border-[#EAE7E0] bg-transparent py-2 focus:border-[#C88270] focus:outline-none" />
          </div>
          <div>
            <label className="block text-[10px] uppercase tracking-[0.25em] text-[#5C5652] mb-2">Team assigned (optional)</label>
            <div className="flex gap-2">
              <select value={assignSel} onChange={(e) => setAssignSel(e.target.value)} data-testid="trial-assign-select"
                className="flex-1 border-b border-[#EAE7E0] bg-transparent py-2 focus:border-[#C88270] focus:outline-none">
                <option value="">Select team member…</option>
                {users.filter((u) => u.role !== "admin").map((u) => (<option key={u.id} value={u.id}>{u.name}</option>))}
              </select>
              <button type="button" onClick={() => {
                if (!assignSel || form.assigned.some((a) => a.team_member_id === assignSel)) return;
                const u = users.find((x) => x.id === assignSel);
                setForm({ ...form, assigned: [...form.assigned, { team_member_id: assignSel, service_type: u?.service_types?.[0] || "trial", agreed_amount: 0 }] });
                setAssignSel("");
              }} className="px-3 py-1 border border-[#2A2421] text-xs uppercase tracking-[0.2em] hover:bg-[#2A2421] hover:text-white transition-colors">Add</button>
            </div>
            {form.assigned.map((a) => {
              const u = users.find((x) => x.id === a.team_member_id);
              return <div key={a.team_member_id} className="flex justify-between items-center py-1 text-sm">
                <span>{u?.name}</span>
                <button type="button" onClick={() => setForm({ ...form, assigned: form.assigned.filter((x) => x.team_member_id !== a.team_member_id) })} className="text-[#5C5652] hover:text-red-700"><X size={14} /></button>
              </div>;
            })}
          </div>
          <div className="flex justify-end gap-3 pt-4 border-t border-[#EAE7E0]">
            <button type="button" onClick={onClose} className="px-6 py-2 text-xs uppercase tracking-[0.2em] text-[#5C5652]">Cancel</button>
            <button type="submit" disabled={saving} data-testid="trial-submit-button"
              className="px-6 py-2 bg-[#C88270] text-white text-xs uppercase tracking-[0.25em] hover:bg-[#B37363] transition-colors disabled:opacity-50">
              {saving ? "Saving…" : "Schedule trial"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default function TrialsPage() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [trials, setTrials] = useState([]);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [weddings, setWeddings] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(false);
  const [sortDir, setSortDir] = useState("asc");
  const [page, setPage] = useState(1);
  const PAGE_SIZE = 15;
  const [searchParams, setSearchParams] = useSearchParams();

  useEffect(() => {
    if (searchParams.get("new") === "1" && isAdmin) {
      setModal(true);
      searchParams.delete("new");
      setSearchParams(searchParams, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, isAdmin]);

  // Weddings + users needed only by admin for the "New Trial" modal
  useEffect(() => {
    if (!isAdmin) return;
    Promise.all([api.get("/weddings"), api.get("/users")])
      .then(([w, u]) => { setWeddings(w.data); setUsers(u.data); })
      .catch(() => { /* ignore */ });
  }, [isAdmin]);

  useEffect(() => { setPage(1); }, [sortDir]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/trials", { params: { page, limit: PAGE_SIZE, sort: sortDir } });
      setTrials(data.items || []);
      setTotal(data.total || 0);
      setTotalPages(data.total_pages || 1);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setLoading(false); }
  }, [page, sortDir]);
  useEffect(() => { load(); }, [load]);

  const refreshAll = async () => {
    if (isAdmin) {
      try {
        const [w, u] = await Promise.all([api.get("/weddings"), api.get("/users")]);
        setWeddings(w.data); setUsers(u.data);
      } catch { /* ignore */ }
    }
    load();
  };

  return (
    <div className="max-w-6xl mx-auto px-6 md:px-12 py-10 md:py-16" data-testid="trials-page">
      <div className="flex items-end justify-between mb-8 flex-wrap gap-4">
        <div>
          <div className="text-[10px] uppercase tracking-[0.3em] text-[#5C5652] mb-3">Studio</div>
          <h1 className="font-serif text-4xl md:text-5xl tracking-tight text-[#2A2421]">Trials</h1>
          <p className="text-sm text-[#5C5652] mt-2 max-w-xl">Schedule makeup / photo trials for any wedding — pending or confirmed. Search leads by couple name or phone.</p>
        </div>
        {isAdmin && (
          <button onClick={() => setModal(true)} data-testid="new-trial-button"
            className="inline-flex items-center gap-2 px-6 py-3 bg-[#C88270] text-white text-xs uppercase tracking-[0.25em] hover:bg-[#B37363] transition-colors">
            <Plus size={16} /> New Trial
          </button>
        )}
      </div>

      {loading ? (
        <div className="text-xs uppercase tracking-[0.3em] text-[#5C5652]">Loading…</div>
      ) : total === 0 ? (
        <div className="p-12 bg-white border border-[#EAE7E0] text-center text-[#5C5652]">No trials scheduled yet.</div>
      ) : (
        <div className="hidden md:block bg-white border border-[#EAE7E0] overflow-x-auto" data-testid="trials-table">
          <table className="w-full text-left">
            <thead>
              <tr className="border-b border-[#EAE7E0]">
                <th className="py-4 px-6 text-[10px] uppercase tracking-[0.25em] text-[#5C5652]">
                  <button onClick={() => setSortDir(sortDir === "asc" ? "desc" : "asc")} className="inline-flex items-center gap-2 hover:text-[#2A2421]" data-testid="sort-trials-date">
                    Date <ArrowUpDown size={12} />
                  </button>
                </th>
                <th className="py-4 px-6 text-[10px] uppercase tracking-[0.25em] text-[#5C5652]">Time</th>
                <th className="py-4 px-6 text-[10px] uppercase tracking-[0.25em] text-[#5C5652]">Couple</th>
                <th className="py-4 px-6 text-[10px] uppercase tracking-[0.25em] text-[#5C5652]">Lead Status</th>
                {isAdmin && <th className="py-4 px-6 text-[10px] uppercase tracking-[0.25em] text-[#5C5652]">Phone</th>}
                <th className="py-4 px-6 text-[10px] uppercase tracking-[0.25em] text-[#5C5652]">Location</th>
                <th className="py-4 px-6"></th>
              </tr>
            </thead>
            <tbody>
              {trials.map((t) => (
                <tr key={t.id} className="border-b border-[#EAE7E0] last:border-0 hover:bg-[#FAF9F6]/60" data-testid={`trial-row-${t.id}`}>
                  <td className="py-4 px-6 text-sm text-[#2A2421] whitespace-nowrap">{formatDate(t.event_date)}</td>
                  <td className="py-4 px-6 text-sm text-[#5C5652]">{t.start_time || "—"}</td>
                  <td className="py-4 px-6 text-sm text-[#2A2421]">{t.wedding?.couple_names || "—"}</td>
                  <td className="py-4 px-6"><StatusPill status={t.wedding?.status} /></td>
                  {isAdmin && <td className="py-4 px-6 text-sm text-[#5C5652]">{t.wedding?.contact_phone || "—"}</td>}
                  <td className="py-4 px-6 text-sm text-[#5C5652]">{t.location || "—"}</td>
                  <td className="py-4 px-6 text-right">
                    <Link to={`/events/${t.id}`} className="text-[10px] uppercase tracking-[0.25em] text-[#C88270] hover:text-[#B37363]" data-testid={`open-trial-${t.id}`}>Open →</Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {!loading && total > 0 && (
        <div className="md:hidden space-y-4" data-testid="trials-mobile-list">
          {trials.map((t) => (
            <Link key={t.id} to={`/events/${t.id}`} data-testid={`trial-mcard-${t.id}`}
              className="block bg-white border border-[#EAE7E0] rounded-[20px] p-5 shadow-[0_2px_12px_-4px_rgba(42,36,33,0.05)] transition-transform active:scale-[0.99]">
              <div className="flex items-start justify-between gap-3">
                <div className="font-serif text-[22px] leading-tight text-[#2A2421] line-clamp-2">{t.wedding?.couple_names || "Trial"}</div>
                <StatusPill status={t.wedding?.status} />
              </div>
              <div className="mt-3 pt-3 border-t border-[#EAE7E0]/60 space-y-1 text-[13px] text-[#5C5652]">
                <div className="flex items-center gap-2">{formatDate(t.event_date)}{t.start_time ? ` · ${t.start_time}` : ""}</div>
                {isAdmin && t.wedding?.contact_phone && <div>{t.wedding.contact_phone}</div>}
                {t.location && <div className="flex items-center gap-2">{t.location}</div>}
              </div>
            </Link>
          ))}
        </div>
      )}

      {!loading && total > 0 && (
        <Pagination page={page} totalPages={totalPages} total={total} pageSize={PAGE_SIZE} onChange={setPage} testId="trials-pagination" />
      )}

      <NewTrialModal open={modal} onClose={() => setModal(false)} onSaved={refreshAll} weddings={weddings} users={users} />
    </div>
  );
}
